import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT } from "../lib/api";
import { BrandLogo } from "./BrandLogo";

const SERVICE_KEYS = ["faults", "panel", "nen", "lighting", "data", "vve"];
const COMPANY_LINKS = [
  { key: "about", href: "/#over-ons" },
  { key: "area", href: "/#werkgebied" },
  { key: "reviews", href: "/#reviews" },
  { key: "projects", href: "/#projecten" },
  { key: "quote", href: "/#offerte" },
];

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer data-testid="site-footer" className="bg-[#050914] text-white border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-14 md:pt-16 pb-10 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <BrandLogo size="md" className="mb-4" />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.25em] text-[#FF5A1F] font-bold mb-5">
            {CONTACT.services.map((s, i) => (
              <span key={s} className="flex items-center gap-2">
                {s}
                {i < CONTACT.services.length - 1 && <span className="text-white/25">|</span>}
              </span>
            ))}
          </div>
          <p className="font-display text-white text-sm uppercase tracking-widest mb-3">
            &ldquo;{CONTACT.tagline}&rdquo;
          </p>
          <p className="text-white/60 text-sm leading-relaxed mb-5">
            {t("footer.tagline_desc", { region: CONTACT.region })}
          </p>
          <div className="flex gap-2" data-testid="footer-socials">
            <a href="#" aria-label="Facebook" className="w-9 h-9 border border-white/15 hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center justify-center text-white/70 transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
            <a href="#" aria-label="Instagram" className="w-9 h-9 border border-white/15 hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center justify-center text-white/70 transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="#" aria-label="LinkedIn" className="w-9 h-9 border border-white/15 hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center justify-center text-white/70 transition-colors">
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm text-[#FF5A1F] uppercase mb-4 tracking-widest">{t("footer.services_title")}</h4>
          <ul className="text-sm text-white/70 space-y-2.5">
            {SERVICE_KEYS.map((k) => (
              <li key={k}><a href="/#diensten" className="hover:text-white transition-colors">{t(`footer.services.${k}`)}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm text-[#FF5A1F] uppercase mb-4 tracking-widest">{t("footer.company_title")}</h4>
          <ul className="text-sm text-white/70 space-y-2.5">
            {COMPANY_LINKS.map((l) => (
              <li key={l.key}><a href={l.href} className="hover:text-white transition-colors">{t(`footer.company.${l.key}`)}</a></li>
            ))}
            <li><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white transition-colors">{t("footer.company.admin")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm text-[#FF5A1F] uppercase mb-4 tracking-widest">{t("footer.contact_title")}</h4>
          <ul className="text-sm text-white/70 space-y-2.5">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#FF5A1F] shrink-0" /> <a href={`tel:${CONTACT.phone}`} className="hover:text-white transition-colors">{CONTACT.phoneDisplay}</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#FF5A1F] shrink-0" /> <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors break-all">{CONTACT.email}</a></li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF5A1F] shrink-0" /> {CONTACT.region}</li>
            <li className="pt-2">KvK: <span className="text-white font-semibold">{CONTACT.kvk}</span></li>
          </ul>
          <div className="mt-5 pt-5 border-t border-white/10">
            <h5 className="font-display text-xs text-white uppercase mb-2 tracking-widest">{t("footer.legal_title")}</h5>
            <ul className="text-xs text-white/60 space-y-1.5">
              <li><Link to="/privacy" className="hover:text-white transition-colors">{t("footer.legal.privacy")}</Link></li>
              <li><Link to="/voorwaarden" className="hover:text-white transition-colors">{t("footer.legal.terms")}</Link></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-white/50">
          <span>&copy; {new Date().getFullYear()} {CONTACT.companyName}. {t("footer.rights")}</span>
          <span>{CONTACT.website}</span>
        </div>
      </div>
    </footer>
  );
}
